import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackSubscriptionStarted, trackSubscriptionCancelled } from '@/utils/amplitudeEvents';

interface SubscriptionContextType {
  subscribed: boolean;
  subscriptionTier: string | null;
  subscriptionEnd: string | null;
  trialEndsAt: string | null;
  isTrialExpired: boolean;
  hasAccess: boolean;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckoutSession: (priceId: string, planName: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  testEdgeFunction: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session } = useAuth();
  const { currentCompany, loading: companyLoading } = useCompany();
  const { toast } = useToast();
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  
  // Track which company we've validated subscription for
  const [validatedForCompanyId, setValidatedForCompanyId] = useState<string | null>(null);
  // Persist lastTier across page loads using localStorage
  const getStoredTier = (): string | null => {
    try { return localStorage.getItem('amplitude_last_subscription_tier'); } catch { return null; }
  };
  const lastTierRef = useRef<string | null>(getStoredTier());

  // Compute derived state
  const isTrialExpired = trialEndsAt ? new Date(trialEndsAt) < new Date() : false;

  // SYNCHRONOUS loading computation - this runs during render, not in useEffect
  // This prevents the race condition where ProtectedRoute sees loading=false before useEffect runs
  const needsSubscriptionCheck = Boolean(
    user && 
    currentCompany?.company_id && 
    validatedForCompanyId !== currentCompany.company_id
  );
  
  // Effective loading state combines all loading conditions
  // Note: We removed waitingForCompany because companyLoading already covers the race condition
  // If user has no companies, companyLoading=false allows ProtectedRoute to redirect to onboarding
  const effectiveLoading = companyLoading || isChecking || needsSubscriptionCheck;

  const logDebug = (step: string, data?: any) => {
    console.log(`[SubscriptionContext] ${step}`, data || '');
  };

  const checkSubscription = useCallback(async () => {
    if (!user?.email) {
      // Reset subscription state for non-authenticated users
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      setTrialEndsAt(null);
      setHasAccess(false);
      setValidatedForCompanyId(null);
      return;
    }

    setIsChecking(true);
    try {
      // PRIMARY: Check company subscription (source of truth for billing)
      if (currentCompany?.company_id) {
        const { data: company, error: companyError } = await supabase
          .from('companies')
          .select('subscription_tier, trial_ends_at, stripe_customer_id, billing_email, has_access, created_at')
          .eq('id', currentCompany.company_id)
          .single();

        if (!companyError && company) {
          const tier = company.subscription_tier || 'trial';
          const trialEnd = company.trial_ends_at;
          
          // Check if trial has expired and update tier + has_access if needed
          if (tier === 'trial' && trialEnd && new Date(trialEnd) < new Date()) {
            // Lazy update: mark trial as expired and revoke access in database
            await supabase
              .from('companies')
              .update({ 
                subscription_tier: 'expired',
                has_access: false 
              })
              .eq('id', currentCompany.company_id);
            
            lastTierRef.current = 'expired';
            try { localStorage.setItem('amplitude_last_subscription_tier', 'expired'); } catch {}
            logDebug('Trial expired, updated to expired tier and revoked access', { companyId: currentCompany.company_id });
            setSubscriptionTier('expired');
            setTrialEndsAt(trialEnd);
            setSubscribed(false);
            setSubscriptionEnd(null);
            setHasAccess(false);
            setValidatedForCompanyId(currentCompany.company_id);
            setIsChecking(false);
            return;
          }
          
          // Use has_access directly from database as source of truth
          const dbHasAccess = company.has_access ?? false;
          const prevTier = lastTierRef.current;
          lastTierRef.current = tier;
          try { localStorage.setItem('amplitude_last_subscription_tier', tier); } catch {}

          if (tier === 'pro' && prevTier === 'trial') {
            // EVENT 4: Subscription Started (detected on next page load)
            trackSubscriptionStarted({
              company_id: currentCompany.company_id,
              plan: 'pro',
              trial_duration_days: trialEnd 
                ? Math.round((new Date(trialEnd).getTime() - new Date(company.created_at).getTime()) / (1000 * 60 * 60 * 24))
                : undefined,
            });
          }

          if (tier === 'expired' && prevTier === 'pro') {
            // EVENT 20: Subscription Cancelled (detected on next page load)
            trackSubscriptionCancelled({
              company_id: currentCompany.company_id,
              previous_plan: 'pro',
              tenure_days: company.created_at
                ? Math.round((Date.now() - new Date(company.created_at).getTime()) / (1000 * 60 * 60 * 24))
                : undefined,
            });
          }
          
          logDebug('Company subscription found', { 
            companyId: currentCompany.company_id, 
            tier, 
            trialEnd,
            hasAccess: dbHasAccess,
            hasStripeCustomer: !!company.stripe_customer_id 
          });
          
          setSubscribed(tier === 'pro');
          setSubscriptionTier(tier);
          setTrialEndsAt(trialEnd);
          setSubscriptionEnd(null);
          setHasAccess(dbHasAccess);
          setValidatedForCompanyId(currentCompany.company_id);
          setIsChecking(false);
          return;
        }
      }

      // Fallback to user-level subscription check for backward compatibility
      const { data: subscriber, error: dbError } = await supabase
        .from('subscribers')
        .select('subscribed, subscription_tier, subscription_end')
        .eq('email', user.email)
        .eq('subscribed', true)
        .maybeSingle();

      if (dbError && dbError.code !== 'PGRST116') {
        console.error('Error checking local subscription:', dbError);
        throw dbError;
      }

      // If we have a local subscription record, use it
      if (subscriber) {
        const fallbackHasAccess = subscriber.subscribed && subscriber.subscription_tier === 'pro';
        setSubscribed(subscriber.subscribed || false);
        setSubscriptionTier(subscriber.subscription_tier || null);
        setSubscriptionEnd(subscriber.subscription_end || null);
        setHasAccess(fallbackHasAccess);
      } else {
        // No local subscription found, check with Stripe via edge function
        if (session?.access_token) {
          const { data, error } = await supabase.functions.invoke('check-subscription', {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });

          if (error) {
            console.error('Error checking Stripe subscription:', error);
            // Don't throw here, just log and continue with no access
            setHasAccess(false);
          } else {
            setSubscribed(data?.subscribed || false);
            setSubscriptionTier(data?.subscription_tier || null);
            setSubscriptionEnd(data?.subscription_end || null);
            setHasAccess(data?.subscribed || false);
          }
        } else {
          // No session, user is not subscribed
          setSubscribed(false);
          setSubscriptionTier(null);
          setSubscriptionEnd(null);
          setHasAccess(false);
        }
      }
      
      // Mark as validated for current company (or null if no company)
      setValidatedForCompanyId(currentCompany?.company_id || 'no-company');
    } catch (error) {
      console.error('Error checking subscription:', error);
      // On error, default to expired tier (no access)
      setSubscribed(false);
      setSubscriptionTier('expired');
      setSubscriptionEnd(null);
      setTrialEndsAt(null);
      setHasAccess(false);
      setValidatedForCompanyId(currentCompany?.company_id || 'error');
    } finally {
      setIsChecking(false);
    }
  }, [user, session, currentCompany, toast]);

  const refreshSession = async (): Promise<boolean> => {
    try {
      logDebug('Refreshing session...');
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('SubscriptionContext: Session refresh failed:', error);
        return false;
      }
      
      if (data.session) {
        logDebug('Session refreshed successfully', {
          hasAccessToken: !!data.session.access_token,
          userEmail: data.session.user?.email
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('SubscriptionContext: Error refreshing session:', error);
      return false;
    }
  };

  const validateSession = async (): Promise<boolean> => {
    try {
      logDebug('Validating current session...');
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('SubscriptionContext: Session validation error:', error);
        return false;
      }

      if (!currentSession?.access_token || !currentSession?.user?.email) {
        logDebug('Invalid session detected, attempting refresh...');
        return await refreshSession();
      }

      logDebug('Session is valid', {
        hasAccessToken: !!currentSession.access_token,
        userEmail: currentSession.user?.email,
        expiresAt: currentSession.expires_at
      });

      return true;
    } catch (error) {
      console.error('SubscriptionContext: Session validation failed:', error);
      return false;
    }
  };

  const testEdgeFunction = useCallback(async () => {
    logDebug('Testing edge function connectivity...');
    
    try {
      if (!session?.access_token) {
        throw new Error('No session available for testing');
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        logDebug('Edge function test failed', error);
        toast({
          title: "Edge Function Test Failed",
          description: `Error: ${error.message}`,
          variant: "destructive"
        });
      } else {
        logDebug('Edge function test successful', data);
      }
    } catch (error: any) {
      logDebug('Edge function test error', error);
      toast({
        title: "Edge Function Test Error",
        description: error.message,
        variant: "destructive"
      });
    }
  }, [session, toast]);

  const createCheckoutSession = useCallback(async (priceId: string, planName: string) => {
    logDebug('Starting checkout session creation', { 
      priceId, 
      planName, 
      hasUser: !!user,
      hasSession: !!session,
      hasAccessToken: !!session?.access_token,
      userEmail: user?.email 
    });

    setIsChecking(true);
    
    try {
      // Step 1: Validate current session
      const isValidSession = await validateSession();
      
      if (!isValidSession) {
        throw new Error('Unable to establish valid session. Please try logging out and back in.');
      }

      // Step 2: Get the most current session
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        logDebug('Session retrieval error', sessionError);
        throw new Error('Session validation failed. Please try logging in again.');
      }

      if (!currentSession?.access_token || !currentSession?.user?.email) {
        throw new Error('No valid authentication session. Please log in again.');
      }

      logDebug('Using validated session for checkout', {
        hasAccessToken: !!currentSession.access_token,
        userEmail: currentSession.user?.email,
        expiresAt: currentSession.expires_at
      });

      // Step 3: Test edge function connectivity first
      logDebug('Testing edge function before checkout...');
      
      const testResponse = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (testResponse.error) {
        logDebug('Edge function test failed before checkout', testResponse.error);
        throw new Error(`Backend connectivity issue: ${testResponse.error.message}`);
      }

      logDebug('Edge function test passed, proceeding with checkout...');

      // Step 4: Create checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId, planName },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (error) {
        logDebug('Checkout creation failed', error);
        throw new Error(error.message || 'Failed to create checkout session');
      }

      logDebug('Checkout session created successfully', data);
      
      // Step 5: Validate and open checkout URL
      if (!data?.url || typeof data.url !== 'string') {
        throw new Error('Invalid checkout URL received from server');
      }

      logDebug('Opening Stripe checkout', { url: data.url });
      
      // Open Stripe checkout in a new tab
      const stripeWindow = window.open(data.url, '_blank');
      
      if (!stripeWindow) {
        // Fallback if popup was blocked
        logDebug('Popup blocked, redirecting in current window');
        window.location.href = data.url;
      }
      
    } catch (error: any) {
      logDebug('Checkout session creation failed', error);
      
      let errorMessage = 'Failed to create checkout session. Please try again.';
      
      if (error.message?.includes('session') || error.message?.includes('authentication')) {
        errorMessage = 'Authentication issue. Please try logging out and back in.';
      } else if (error.message?.includes('network') || error.message?.includes('connectivity')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message?.includes('Backend connectivity')) {
        errorMessage = 'Backend service issue. Please try again in a moment.';
      }
      
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsChecking(false);
    }
  }, [user, session, toast]);

  const openCustomerPortal = useCallback(async () => {
    if (!session?.access_token) {
      toast({
        title: "Authentication Required",
        description: "Please log in to continue",
        variant: "destructive"
      });
      return;
    }

    setIsChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Open customer portal in a new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: "Failed to open customer portal",
        variant: "destructive"
      });
    } finally {
      setIsChecking(false);
    }
  }, [session, toast]);

  useEffect(() => {
    // Wait for company context to finish loading before checking subscription
    if (companyLoading) {
      return;
    }
    
    if (user) {
      // Only check if we haven't validated for this company yet
      if (needsSubscriptionCheck) {
        logDebug('Starting subscription check', { 
          userId: user.id, 
          companyId: currentCompany?.company_id,
          validatedFor: validatedForCompanyId 
        });
        checkSubscription();
      }
    } else {
      // Reset state when user logs out
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      setTrialEndsAt(null);
      setHasAccess(false);
      setValidatedForCompanyId(null);
      setIsChecking(false);
    }
  }, [user, currentCompany?.company_id, companyLoading, needsSubscriptionCheck, checkSubscription]);

  const value = useMemo(() => ({
    subscribed,
    subscriptionTier,
    subscriptionEnd,
    trialEndsAt,
    isTrialExpired,
    hasAccess,
    loading: effectiveLoading,
    checkSubscription,
    createCheckoutSession,
    openCustomerPortal,
    testEdgeFunction,
  }), [subscribed, subscriptionTier, subscriptionEnd, trialEndsAt, isTrialExpired, hasAccess, effectiveLoading, checkSubscription, createCheckoutSession, openCustomerPortal, testEdgeFunction]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
